<template>
  <div class="landing-demo-table">
    <h3 class="landing-demo-table__heading">
      Список проанализированных тендеров — {{ rows.length }}
    </h3>
    <div class="landing-demo-table__wrapper" role="region" aria-label="Демо-таблица тендеров">
      <table class="tender-table__table">
        <thead>
          <tr>
            <th>Название</th>
            <th>Способ отбора</th>
            <th>Цена</th>
            <th>Дата аукциона</th>
            <th>Критерии</th>
            <th>% Победы</th>
            <th>Комментарий</th>
            <th class="tender-table__status-col">Статус в системе</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.id"
            class="tender-table__row row-click"
            :class="{
              'tender-table__row--high-probability': isHighProbability(row),
              'tender-table__row--high-margin': isHighMargin(row),
              'tender-table__row--high-score': isHighProbability(row) && isHighMargin(row),
              'tender-table__row--no-docs': row.hasDocuments === false,
            }"
            @click="$emit('open-details', row)"
          >
            <td>
              <div class="tender-table__title">
                <strong class="tender-table__title-main">{{ row.title }}</strong>
                <span v-if="row.customer" class="tender-table__subtitle">{{ row.customer }}</span>
                <span v-if="row.region" class="tender-table__subtitle tender-table__region">{{ row.region }}</span>
              </div>
            </td>
            <td>{{ row.selectionMethod || '—' }}</td>
            <td>
              <div class="tender-table__price">
                <span>{{ formatCurrency(row.budget) }}</span>
                <span
                  class="tender-table__margin"
                  :class="{
                    'tender-table__margin--good': row.marginIsGood,
                    'tender-table__margin--negative': row.marginValue != null && row.marginValue < 0,
                    'tender-table__margin--muted': (row.marginText ?? '—') === '—',
                  }"
                >
                  Маржа: {{ row.marginText ?? '—' }}
                </span>
              </div>
            </td>
            <td>{{ formatDate(row.auctionDate) }}</td>
            <td>
              <ul v-if="row.criteriaSummary?.length" class="tender-table__criteria">
                <li v-for="(line, idx) in row.criteriaSummary" :key="`${row.id}-c-${idx}`">{{ line }}</li>
              </ul>
              <span v-else>—</span>
            </td>
            <td>{{ formatProbability(row.probability) }}</td>
            <td>
              <div class="tender-table__comment">
                <span v-if="row.commentText">{{ row.commentText }}</span>
                <span v-else>—</span>
              </div>
            </td>
            <td class="tender-table__status">
              <span
                v-if="row.kanbanStatusLabel"
                class="tender-table__status-badge"
                :style="{ '--stage-color': row.kanbanStatusColor || '#64748b' }"
              >
                <span class="tender-table__status-dot" aria-hidden="true" />
                {{ row.kanbanStatusLabel }}
              </span>
              <span v-else class="text-muted">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="lockedCount > 0" class="landing-demo-table__locked">
      <p>Ещё {{ lockedCount }} тендеров доступны после получения доступа</p>
      <button type="button" class="landing-access-cta" @click="$emit('open-quiz')">Получить доступ</button>
    </div>
  </div>
</template>

<script setup>
import '@/styles/landing-access-cta.css'
import { formatDate } from '@/landing/demoScenarios'

defineProps({
  rows: { type: Array, default: () => [] },
  lockedCount: { type: Number, default: 0 },
})

defineEmits(['open-details', 'open-quiz'])

function isHighProbability(row) {
  const value = Number(row?.probability)
  return Number.isFinite(value) && value >= 70
}

function isHighMargin(row) {
  return Boolean(row?.marginIsGood)
}

const formatCurrency = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—'
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(value)
}

const formatProbability = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—'
  return `${value}%`
}
</script>

<style scoped>
.landing-demo-table__heading {
  margin: 0 0 0.75rem;
  font-size: 1rem;
  font-weight: 600;
  color: var(--fg, #0f172a);
  text-align: center;
}

.landing-demo-table__wrapper {
  overflow-x: auto;
  border: 1px solid var(--border-strong, #cbd5e1);
  border-radius: 12px;
  background: #fff;
}

.tender-table__table {
  width: 100%;
  min-width: 820px;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.tender-table__table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f1f5f9;
  padding: 0.65rem 0.75rem;
  text-align: left;
  font-weight: 600;
  color: #334155;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.tender-table__table tbody td {
  padding: 0.65rem 0.75rem;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: top;
}

.tender-table__row {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.tender-table__row:hover {
  background: #f8fafc;
}

.tender-table__row--high-probability {
  background: rgba(16, 185, 129, 0.08);
}

.tender-table__row--high-margin {
  background: rgba(59, 130, 246, 0.08);
}

.tender-table__row--high-score {
  background: linear-gradient(90deg, rgba(16, 185, 129, 0.12), rgba(59, 130, 246, 0.12));
}

.tender-table__row--no-docs {
  background: rgba(248, 215, 218, 0.45);
}

.tender-table__title {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tender-table__title-main {
  font-weight: 600;
  color: #0f172a;
}

.tender-table__subtitle {
  display: block;
  font-size: 13px;
  color: #64748b;
}

.tender-table__region {
  font-weight: 600;
  color: #0ea5e9;
}

.tender-table__price {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tender-table__price > span:first-child {
  font-weight: 600;
  color: #0f172a;
}

.tender-table__margin {
  font-size: 12px;
  color: #64748b;
}

.tender-table__margin--good {
  color: #15803d;
  font-weight: 600;
}

.tender-table__margin--negative {
  color: #dc2626;
}

.tender-table__margin--muted {
  color: #94a3b8;
}

.tender-table__criteria {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: #475569;
  max-width: 220px;
}

.tender-table__comment {
  white-space: normal;
  word-break: break-word;
  font-size: 13px;
  color: #1f2937;
  max-width: 200px;
}

.tender-table__status-col {
  width: 160px;
}

.tender-table__status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  max-width: 100%;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 500;
  background: color-mix(in srgb, var(--stage-color) 14%, #fff);
  color: #1e293b;
  white-space: nowrap;
}

.tender-table__status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: var(--stage-color);
}

.landing-demo-table__locked {
  margin-top: 1rem;
  padding: 1rem;
  text-align: center;
  border: 1px dashed var(--border-strong, #cbd5e1);
  border-radius: 12px;
  background: #f8fafc;
}

.landing-demo-table__locked p {
  margin: 0 0 0.75rem;
  font-weight: 600;
  color: var(--fg, #0f172a);
}
</style>
